/**
 * Oh Hell game view.
 *
 * Reuses the same generic primitives as the Hearts and Sheng Ji views:
 *   - `CircleTable` lays out seats around the table.
 *   - `Hand` renders the player's cards as a scrollable fan.
 *   - `PlayingCard` renders the trump card and trick plays.
 *
 * Game-specific bits: the bidding action bar with the dealer's hook hint,
 * the per-seat bid/tricks-taken nameplate, and the running scorecard.
 */

import { useMemo, useState } from "react";
import {
  lerpCardWidth,
  useDynamicTableTop,
  useResetOnChange,
  useWindowWidth,
} from "../../lib/uiHooks.js";
import type {
  OhHellPublicState,
} from "../../../../shared/games/ohhell.js";
import type { CardId, SuitId } from "../../../../shared/games/shengji.js";
import type { ClientMsg, GameEvent } from "../../../../shared/protocol.js";
import { CircleTable } from "../../components/CircleTable.js";
import { Hand } from "../../components/Hand.js";
import { PlayingCard } from "../../components/PlayingCard.js";

interface OhHellGameProps {
  state: OhHellPublicState;
  yourHand: CardId[];
  yourSeat: number | null;
  events: GameEvent[];
  send: (m: ClientMsg) => void;
  mobile?: boolean;
}

export function OhHellGame({
  state,
  yourHand,
  yourSeat,
  send,
  mobile = false,
}: OhHellGameProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [scoresOpen, setScoresOpen] = useState(!mobile);

  // Reset selection on phase/trick changes.
  const phaseKey = `${state.phase}:${state.roundNumber}:${state.currentTrickPlays.length}:${state.completedTricks.length}`;
  useResetOnChange(phaseKey, () => setSelected(new Set()));

  const windowWidth = useWindowWidth();
  const playZoneCardWidth = mobile ? 56 : lerpCardWidth(windowWidth, 90, 132);

  const legal = useMemo(() => {
    if (yourSeat === null) return new Set<CardId>();
    if (state.phase !== "play") return new Set(yourHand);
    if (state.currentSeat !== yourSeat) return new Set<CardId>();
    return computeOhHellLegal(yourHand, state);
  }, [yourHand, state, yourSeat]);

  const selectedCardIds = useMemo<CardId[]>(
    () => [...selected].map((i) => yourHand[i]!).filter(Boolean),
    [selected, yourHand]
  );

  function toggle(i: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      // Limit to one selection for play.
      if (state.phase === "play" && next.size > 1) {
        for (const x of [...next]) if (x !== i) next.delete(x);
      }
      return next;
    });
  }

  const myTurnToBid =
    state.phase === "bidding" && yourSeat !== null && state.currentSeat === yourSeat;
  const myTurnToPlay =
    state.phase === "play" && yourSeat !== null && state.currentSeat === yourSeat;
  const tableTopRef = useDynamicTableTop();

  return (
    <div className="game-view" ref={tableTopRef}>
      <CircleTable
        seatCount={state.players.length}
        mySeatIndex={yourSeat}
        renderSeat={(i) => (
          <OhHellSeatView
            seat={i}
            state={state}
            yourSeat={yourSeat}
            playZoneCardWidth={playZoneCardWidth}
          />
        )}
      >
        <OhHellCenter state={state} cardWidth={playZoneCardWidth} />
      </CircleTable>

      <button
        className="scoreboard-toggle"
        onClick={() => setScoresOpen((v) => !v)}
        aria-pressed={scoresOpen}
      >
        {scoresOpen ? "Hide scores" : "Show scores"}
      </button>
      {scoresOpen && <OhHellScorePanel state={state} yourSeat={yourSeat} />}

      {myTurnToBid && (
        <BidActionBar state={state} yourSeat={yourSeat!} send={send} />
      )}
      {myTurnToPlay && (
        <PlayActionBar
          selectedCard={selectedCardIds[0] ?? null}
          send={send}
          onAfterSubmit={() => setSelected(new Set())}
        />
      )}
      {yourSeat !== null && state.phase === "round_end" && (
        <ReadyActionBar send={send} />
      )}

      {yourSeat !== null && (
        <Hand
          cards={yourHand}
          legal={state.phase === "play" ? legal : undefined}
          selected={selected}
          onToggle={toggle}
          trumpSuit={state.trumpSuit ?? undefined}
          mobile={mobile}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Seat view
// ---------------------------------------------------------------------------

function OhHellSeatView({
  seat,
  state,
  yourSeat,
  playZoneCardWidth,
}: {
  seat: number;
  state: OhHellPublicState;
  yourSeat: number | null;
  playZoneCardWidth: number;
}) {
  const p = state.players[seat]!;
  const isMe = seat === yourSeat;
  const isTurn =
    (state.phase === "bidding" || state.phase === "play") &&
    state.currentSeat === seat;
  const isDealer = seat === state.dealer;
  const trickPlay = state.currentTrickPlays.find((tp) => tp.seat === seat);
  const isWinning =
    state.currentTrickPlays.length > 0 &&
    state.currentTrickPlays[state.currentTrickWinningIndex]?.seat === seat;
  return (
    <>
      <div
        className={[
          "nameplate",
          isMe ? "mine" : "opponent",
          isTurn ? "turn" : "",
        ].filter(Boolean).join(" ")}
      >
        <div className="nameplate-row">
          {isMe && <span className="role-tag mine">YOU</span>}
          <span className={`conn-dot ${p.connected ? "" : "off"}`} />
          <span className="player-name">{p.displayName}</span>
          {isDealer && <span className="role-tag">DEALER</span>}
        </div>
        <div className="nameplate-row sub">
          <span className="rank-line">
            {p.bid === null ? "—" : `bid ${p.bid}`} · won {p.tricksTaken}
          </span>
          <span className="rank-line">total {p.totalScore}</span>
        </div>
      </div>
      <div className={`play-zone ${isWinning ? "winning" : ""}`}>
        {isWinning && <div className="winning-badge">WINNING</div>}
        {trickPlay && (
          <div className="play-card-wrapper" style={{ position: "relative", zIndex: 1 }}>
            <PlayingCard card={trickPlay.card} width={playZoneCardWidth} />
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Centre area
// ---------------------------------------------------------------------------

function OhHellCenter({
  state,
  cardWidth,
}: {
  state: OhHellPublicState;
  cardWidth: number;
}) {
  if (state.phase === "match_end") {
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Match complete</span>
        <span className="points">Game over</span>
      </div>
    );
  }
  if (state.phase === "round_end") {
    const last = state.history[state.history.length - 1];
    return (
      <div className="center-pile" style={{ flexDirection: "column", padding: 14 }}>
        <span className="label">Round complete</span>
        {last && (
          <span className="points" style={{ fontSize: 14 }}>
            {state.players.map((p, i) =>
              `${p.displayName} ${last.bids[i]}/${last.taken[i]} → +${last.delta[i]}`
            ).join(" · ")}
          </span>
        )}
      </div>
    );
  }
  // bidding / play: show the trump card.
  return (
    <div className="center-pile" style={{ flexDirection: "column", padding: 10, gap: 6 }}>
      <span className="label">
        Round {state.roundNumber}/{state.totalRounds} · {state.handSize} card{state.handSize === 1 ? "" : "s"}
      </span>
      {state.trumpCard ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <PlayingCard card={state.trumpCard} width={Math.round(cardWidth * 0.62)} />
          <span className="label" style={{ fontSize: 12 }}>TRUMP</span>
        </div>
      ) : (
        <span className="label">No trump this round</span>
      )}
      {state.phase === "bidding" && (
        <span className="label" style={{ fontSize: 12 }}>
          bids {state.bidsTotal}/{state.handSize}
          {state.hookForbidden !== null && state.currentSeat === state.dealer && (
            <> · dealer can't bid {state.hookForbidden}</>
          )}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Score panel — running scorecard
// ---------------------------------------------------------------------------

function OhHellScorePanel({
  state,
  yourSeat,
}: {
  state: OhHellPublicState;
  yourSeat: number | null;
}) {
  return (
    <div className="hearts-score-panel">
      <div className="hearts-score-header">
        <span className="muted">
          Round {state.roundNumber}/{state.totalRounds}
        </span>
      </div>
      <table className="hearts-score-table">
        <thead>
          <tr>
            <th>Player</th>
            <th style={{ textAlign: "right" }}>Bid</th>
            <th style={{ textAlign: "right" }}>Won</th>
            <th style={{ textAlign: "right" }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {state.players.map((p) => (
            <tr key={p.seat} className={p.seat === yourSeat ? "you" : ""}>
              <td>{p.displayName}</td>
              <td style={{ textAlign: "right" }}>{p.bid === null ? "—" : p.bid}</td>
              <td style={{ textAlign: "right" }}>{p.tricksTaken}</td>
              <td style={{ textAlign: "right" }}>{p.totalScore}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bidding bar
// ---------------------------------------------------------------------------

function BidActionBar({
  state,
  yourSeat,
  send,
}: {
  state: OhHellPublicState;
  yourSeat: number;
  send: (m: ClientMsg) => void;
}) {
  const isDealer = yourSeat === state.dealer;
  const forbidden = isDealer ? state.hookForbidden : null;
  const options = [];
  for (let i = 0; i <= state.handSize; i++) options.push(i);
  return (
    <div className="action-bar" style={{ flexWrap: "wrap", justifyContent: "center", gap: 6 }}>
      <span className="muted" style={{ fontSize: 13, marginRight: 6 }}>
        Bid the number of tricks you'll take:
      </span>
      {options.map((b) => {
        const disabled = forbidden === b;
        return (
          <button
            key={b}
            disabled={disabled}
            title={disabled ? `Forbidden by the hook (total bids would equal ${state.handSize})` : ""}
            onClick={() =>
              send({ t: "action", action: { kind: "ohhell", payload: { type: "bid", bid: b } } })
            }
          >
            {b}
          </button>
        );
      })}
    </div>
  );
}

function PlayActionBar({
  selectedCard,
  send,
  onAfterSubmit,
}: {
  selectedCard: CardId | null;
  send: (m: ClientMsg) => void;
  onAfterSubmit: () => void;
}) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        disabled={!selectedCard}
        onClick={() => {
          if (!selectedCard) return;
          send({
            t: "action",
            action: { kind: "ohhell", payload: { type: "play", card: selectedCard } },
          });
          onAfterSubmit();
        }}
      >
        Play card
      </button>
    </div>
  );
}

function ReadyActionBar({ send }: { send: (m: ClientMsg) => void }) {
  return (
    <div className="action-bar">
      <button
        className="primary"
        onClick={() =>
          send({ t: "action", action: { kind: "ohhell", payload: { type: "ready_next_round" } } })
        }
      >
        Ready for next round
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legality hint
// ---------------------------------------------------------------------------

function computeOhHellLegal(
  hand: CardId[],
  state: OhHellPublicState
): Set<CardId> {
  const out = new Set<CardId>();
  const trick = state.currentTrickPlays;
  if (trick.length === 0) {
    for (const c of hand) out.add(c);
    return out;
  }
  const ledSuit: SuitId = trick[0]!.card[1] as SuitId;
  const inSuit = hand.filter((c) => c[1] === ledSuit);
  if (inSuit.length > 0) {
    for (const c of inSuit) out.add(c);
  } else {
    for (const c of hand) out.add(c);
  }
  return out;
}
